import React, { useEffect, useRef, useState } from 'react';
import { Box, Flex, Text, useColorModeValue } from '@chakra-ui/react';
import ReactApexChart from 'react-apexcharts';

// ── Ring configuration ────────────────────────────────────────────────────────

export interface LayeredDonutRing {
  /** 0–100 progress value. Decorative rings always render as a full 360° circle. */
  value: number;
  /** Fill color for the arc. */
  color: string;
  /** Label shown in the hover tooltip. */
  label?: string;
  /** Override the value shown in the tooltip (e.g. the real % before normalization). */
  displayValue?: string;
  /** When true, renders at 100% always with a transparent track — used for the
   *  outer decorative white border ring. */
  isDecorative?: boolean;
}

// ── Component props ───────────────────────────────────────────────────────────

export interface LayeredDonutProps {
  /** Ordered list of rings — index 0 = outermost. */
  rings: LayeredDonutRing[];
  /** Text rendered in the hollow center (e.g. "44%", "—"). */
  centerLabel: string;
  /**
   * Fixed pixel size for the square chart container.
   * Omit to have the chart fill 100% of its parent's width (responsive).
   */
  size?: number;
  /**
   * ApexCharts `hollow.size` — percentage of chart diameter left empty at center.
   * Defaults to '38%'. Use '65%' for a single-ring gauge with a large center.
   */
  hollowPercent?: string;
  /** Chakra `fontSize` token for the center label. Defaults to 'xl'. */
  centerFontSize?: string;
}

// ── Static track shade arrays ─────────────────────────────────────────────────

const LIGHT_GRAYS = ['#E8E9EF', '#D0D2DA', '#B8BBC6', '#A0A3B1', '#8B8FA0'];
const DARK_GRAYS  = ['#2D3448', '#353D54', '#3D4660', '#455070', '#4D5A80'];

// ── LayeredDonut ──────────────────────────────────────────────────────────────

export const LayeredDonut: React.FC<LayeredDonutProps> = ({
  rings,
  centerLabel,
  size,
  hollowPercent   = '38%',
  centerFontSize  = 'xl',
}) => {
  const innerBg          = useColorModeValue('white', '#1a2035');
  const centerLabelColor = useColorModeValue('#1B2559', 'white');
  const trackGrays       = useColorModeValue(LIGHT_GRAYS, DARK_GRAYS);

  // ── Responsive sizing ────────────────────────────────────────────────────
  // Measure the container's rendered width so the chart and center circle
  // always use the same coordinate space regardless of parent layout changes.
  const containerRef                  = useRef<HTMLDivElement>(null);
  const [chartSize, setChartSize]     = useState<number>(size ?? 220);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0].contentRect.width);
      if (w > 0) setChartSize(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Build per-ring arrays from config ────────────────────────────────────
  const series: number[]        = [];
  const colors: string[]        = [];
  const labels: string[]        = [];
  const displayValues: string[] = [];
  const trackBgs: string[]      = [];
  let dataIdx = 0;

  for (const ring of rings) {
    series.push(ring.isDecorative ? 100 : ring.value);
    colors.push(ring.color);
    labels.push(ring.label ?? '');
    displayValues.push(ring.displayValue ?? `${ring.value}%`);
    trackBgs.push(
      ring.isDecorative
        ? 'transparent'
        : (trackGrays[dataIdx++] ?? trackGrays[trackGrays.length - 1]),
    );
  }

  // Rings that have a label and are not decorative are hoverable
  const hasHoverableRings = rings.some((r) => r.label && !r.isDecorative);

  const options: ApexCharts.ApexOptions = {
    chart: {
      type: 'radialBar',
      background: 'transparent',
      toolbar: { show: false },
      sparkline: { enabled: true },
      animations: { enabled: true, speed: 900, easing: 'easeinout' },
      dropShadow: {
        enabled: true,
        blur: 4,
        opacity: 0.18,
        top: 0,
        left: 0,
        color: '#000000',
      },
    },
    colors,
    labels,
    plotOptions: {
      radialBar: {
        startAngle: 0,
        endAngle: 360,
        hollow: {
          size: hollowPercent,
          background: 'transparent',
        },
        track: {
          background: trackBgs as any,
          strokeWidth: '100%',
          margin: 0,
        },
        dataLabels: { show: false },
      },
    },
    stroke: { lineCap: 'round' },
    legend: { show: false },
    tooltip: {
      enabled: hasHoverableRings,
      custom: ({ seriesIndex }: { seriesIndex: number }) => {
        const label = labels[seriesIndex];
        const display = displayValues[seriesIndex];
        if (!label) return '';
        return `<div style="
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          padding: 6px 12px;
          font-size: 13px;
          font-weight: 600;
          color: ${colors[seriesIndex]};
          box-shadow: 0 2px 8px rgba(0,0,0,0.12);
          white-space: nowrap;
        ">${label}: ${display}</div>`;
      },
    },
    states: {
      hover:  { filter: { type: hasHoverableRings ? 'lighten' : 'none', value: 0.1 } },
      active: { filter: { type: 'none' } },
    },
  };

  return (
    <Box
      ref={containerRef}
      // If `size` is provided use that fixed px square; otherwise fill parent width.
      w={size ? `${size}px` : '100%'}
      sx={{ aspectRatio: '1 / 1' }}
      position="relative"
      flexShrink={0}
    >
      {/* Chart — width/height driven by measured chartSize so they always match the Box */}
      <ReactApexChart
        key={centerLabel}
        options={options}
        series={series}
        type="radialBar"
        width={chartSize}
        height={chartSize}
      />

      {/* Center label — text only, centered in the hollow. The card's white
          background shows through the transparent hollow. */}
      <Flex
        position="absolute"
        inset="0"
        align="center"
        justify="center"
        pointerEvents="none"
      >
        <Text
          fontSize={centerFontSize}
          fontWeight="800"
          color={centerLabelColor}
          lineHeight="1"
          letterSpacing="-0.03em"
          textAlign="center"
          bg={innerBg}
          px="4px"
        >
          {centerLabel}
        </Text>
      </Flex>
    </Box>
  );
};
